import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import {
  forgotPasswordVerifyAccount,
  forgotPasswordSendOtp,
  forgotPasswordVerifyOtp,
  forgotPasswordSetPassword,
} from '../api/mobileRegistrationApi';
import AuthShell from '../components/auth/AuthShell';
import AuthInput from '../components/auth/AuthInput';
import AuthButton from '../components/auth/AuthButton';
import AuthAlert from '../components/auth/AuthAlert';
import { cardVariants } from '../components/auth/authMotion';
import usePrefersReducedMotion from '../hooks/usePrefersReducedMotion';
import { KeyRound, Mail, Smartphone, User } from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const STEP_COPY = {
  1: {
    title: 'Forgot password',
    body: 'Verify your username and email to begin password recovery.',
  },
  2: {
    title: 'Confirm mobile number',
    body: 'Enter the mobile number on file to receive a one-time verification code.',
  },
  3: {
    title: 'Enter verification code',
    body: 'Enter the 6-digit code sent to your mobile number.',
  },
  4: {
    title: 'Set new password',
    body: 'Choose a new password with at least 8 characters.',
  },
  5: {
    title: 'Password updated',
    body: 'You can now sign in with your new password.',
  },
};

export default function ForgotPasswordPage() {
  const reducedMotion = usePrefersReducedMotion();
  const [step, setStep] = useState(1);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');

  const [flowToken, setFlowToken] = useState('');
  const [passwordResetToken, setPasswordResetToken] = useState('');

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [err, setErr] = useState(null);

  const resetMessages = () => {
    setErr(null);
    setMessage(null);
  };

  const variants = reducedMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { duration: 0 } },
        exit: { opacity: 0, transition: { duration: 0 } },
      }
    : cardVariants;

  const onVerifyAccount = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!API_BASE_URL) {
      setErr('API URL is not configured.');
      return;
    }
    setLoading(true);
    try {
      const data = await forgotPasswordVerifyAccount(API_BASE_URL, username.trim(), email.trim());
      setFlowToken(data.flow_token);
      setMessage(data.message || 'Continue with your mobile number.');
      setStep(2);
    } catch (e2) {
      setErr(e2.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const onSendOtp = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!flowToken) {
      setErr('Session expired. Start again from step 1.');
      return;
    }
    setLoading(true);
    try {
      const data = await forgotPasswordSendOtp(API_BASE_URL, flowToken, phone.trim());
      setMessage(data.message || 'Code sent. Enter it below.');
      setStep(3);
    } catch (e2) {
      setErr(e2.message || 'Could not send code');
    } finally {
      setLoading(false);
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    resetMessages();
    if (!flowToken) {
      setErr('Session expired. Start again from step 1.');
      return;
    }
    setLoading(true);
    try {
      const data = await forgotPasswordVerifyOtp(API_BASE_URL, flowToken, otp.trim());
      setPasswordResetToken(data.password_reset_token);
      setMessage(data.message || 'Choose a new password.');
      setStep(4);
    } catch (e2) {
      setErr(e2.message || 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const onSetPassword = async (e) => {
    e.preventDefault();
    resetMessages();
    if (password.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (password !== password2) {
      setErr('Passwords do not match.');
      return;
    }
    if (!passwordResetToken) {
      setErr('Session expired. Start again from step 1.');
      return;
    }
    setLoading(true);
    try {
      const data = await forgotPasswordSetPassword(API_BASE_URL, passwordResetToken, password);
      setMessage(data.message || 'Password updated.');
      setStep(5);
    } catch (e2) {
      setErr(e2.message || 'Could not update password');
    } finally {
      setLoading(false);
    }
  };

  const copy = STEP_COPY[step] || STEP_COPY[1];

  return (
    <AuthShell
      showBeat={false}
      subtitle="Secure account recovery for authorized Vitra users."
    >
      <motion.div
        variants={variants}
        initial="hidden"
        animate="visible"
        className="w-full rounded-2xl border border-gray-100 bg-white p-6 shadow-xl shadow-gray-900/5 sm:p-8 md:p-10"
      >
        <div className="mb-2 flex items-center gap-2">
          {[1, 2, 3, 4].map((n) => (
            <div
              key={n}
              className={`h-1.5 flex-1 rounded-full transition-colors duration-200 ${
                step >= n && step < 5 ? 'bg-teal-600' : step === 5 ? 'bg-teal-600' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className="mb-6 text-xs font-medium text-gray-400">
          {step === 5 ? 'Complete' : `Step ${Math.min(step, 4)} of 4`}
        </p>

        <div className="mb-6">
          <h1 className="text-2xl font-bold tracking-tight text-gray-900">{copy.title}</h1>
          <p className="mt-2 text-sm text-gray-500">{copy.body}</p>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {err && <AuthAlert key="err" type="error" message={err} />}
          {message && step !== 5 && (
            <AuthAlert key="msg" type="success" message={message} />
          )}
        </AnimatePresence>

        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={step}
            initial={reducedMotion ? false : { opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reducedMotion ? undefined : { opacity: 0, y: -4 }}
            transition={{ duration: reducedMotion ? 0 : 0.2, ease: [0.23, 1, 0.32, 1] }}
          >
            {step === 1 && (
              <form onSubmit={onVerifyAccount} className="flex flex-col gap-5">
                <AuthInput
                  label="Username"
                  name="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                  icon={User}
                />
                <AuthInput
                  label="Email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  icon={Mail}
                />
                <AuthButton type="submit" loading={loading}>
                  Continue
                </AuthButton>
              </form>
            )}

            {step === 2 && (
              <form onSubmit={onSendOtp} className="flex flex-col gap-5">
                <AuthInput
                  label="Mobile number"
                  name="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                  placeholder="e.g. 05xxxxxxxx or 9665xxxxxxxx"
                  autoComplete="tel"
                  icon={Smartphone}
                />
                <AuthButton type="submit" loading={loading}>
                  Send verification code
                </AuthButton>
                <button
                  type="button"
                  onClick={() => {
                    setStep(1);
                    setFlowToken('');
                    resetMessages();
                  }}
                  className="text-sm font-semibold text-teal-700 hover:underline"
                >
                  ← Back
                </button>
              </form>
            )}

            {step === 3 && (
              <form onSubmit={onVerifyOtp} className="flex flex-col gap-5">
                <AuthInput
                  label="6-digit code"
                  name="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  placeholder="••••••"
                  icon={KeyRound}
                />
                <AuthButton type="submit" loading={loading}>
                  Verify code
                </AuthButton>
                <button
                  type="button"
                  onClick={() => {
                    setStep(2);
                    setOtp('');
                    setErr(null);
                  }}
                  className="text-sm font-semibold text-teal-700 hover:underline"
                >
                  ← Back
                </button>
              </form>
            )}

            {step === 4 && (
              <form onSubmit={onSetPassword} className="flex flex-col gap-5">
                <AuthInput
                  label="New password"
                  name="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  icon={KeyRound}
                />
                <AuthInput
                  label="Confirm password"
                  name="password2"
                  type="password"
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  required
                  autoComplete="new-password"
                  icon={KeyRound}
                />
                <AuthButton type="submit" loading={loading}>
                  Update password
                </AuthButton>
              </form>
            )}

            {step === 5 && (
              <div className="space-y-5">
                <AuthAlert type="success" message={message || 'Password updated.'} />
                <Link
                  to="/"
                  className="inline-flex w-full items-center justify-center rounded-xl bg-teal-600 px-4 py-3.5 text-base font-semibold text-white shadow-md shadow-teal-600/25 transition-[transform,background-color] duration-150 hover:bg-teal-700 active:scale-[0.97]"
                >
                  Back to sign in
                </Link>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {step < 5 && (
          <p className="mt-8 border-t border-gray-100 pt-6 text-center text-sm text-gray-500">
            <Link to="/" className="font-semibold text-teal-700 hover:underline">
              Back to sign in
            </Link>
          </p>
        )}
      </motion.div>
    </AuthShell>
  );
}
